import { useCallback, useMemo, useState } from "react"

import { InfoOutlineIcon } from "@l2r-front/l2r-icons"
import { FormControl, MenuItem, Typography, useIsMobileDevice } from "@l2r-front/l2r-ui"

import * as Styled from "./Legend.styled"

type LegendItem = {
    color: string,
    label: string | string[],
}

type LegendEntry = {
    layerName: string
    title: string,
    legendData: LegendItem[],
}

export type LegendProps = {
    displayedLegends: LegendEntry[],
    openByDefault?: boolean,
    onToggleLegend?: (open: boolean) => void,
}

export function Legend(props: LegendProps) {

    const {
        displayedLegends,
        openByDefault = false,
        onToggleLegend,

    } = props

    const [open, setOpen] = useState(openByDefault)
    const [selectedLegendIndex, setSelectedLegendIndex] = useState(0)
    const isMobile = useIsMobileDevice()

    const selectOptions = useMemo(() => {
        return displayedLegends.map((v, index) => <MenuItem key={`select-option-${index}`} value={index}>
            {v.layerName}
        </MenuItem>)
    }, [displayedLegends])

    const Title = useMemo(() => {
        return displayedLegends.length > 1 ? () => <FormControl fullWidth>
            <Styled.Select
                noMargin
                onChange={(event) => setSelectedLegendIndex(event.target.value)}
                value={selectedLegendIndex}
                valueVariant="Desktop & Mobile/Heading/H4"
            >
                {selectOptions}
            </Styled.Select>
        </FormControl> : () => <Styled.TitleWrapper>
            <Typography id="map-legend-title" variant="Desktop & Mobile/Heading/H4">
                {displayedLegends?.[0]?.title}
            </Typography>
        </Styled.TitleWrapper>
    }, [displayedLegends, selectOptions, selectedLegendIndex])

    const displayLabel = useCallback((label) => {
        if (Array.isArray(label)) {
            return <Styled.LabelWrapper>
                {
                    label.map((labelValue => {
                        return <Styled.Label variant="Desktop & Mobile/Body/Regular">{labelValue}</Styled.Label>
                    }))
                }
            </Styled.LabelWrapper>
        }
        return <Styled.Label variant="Desktop & Mobile/Body/Regular">{label}</Styled.Label>
    }, [])

    const handleToggleLegend = useCallback(() => {
        setOpen((prev) => {
            const newState = !prev
            onToggleLegend?.(newState)
            return newState
        })
    }, [onToggleLegend, setOpen])

    if (!open) {
        return <Styled.Button
            variant="tertiary"
            shape="circle"
            outlined={false}
            id="map-legend-open-button"
            onClick={handleToggleLegend}
        >
            <InfoOutlineIcon />
        </Styled.Button>
    }

    return <Styled.LegendWrapper id="map-legend" isMobile={isMobile} >
        <Styled.LegendHeader>
            <Title />
            <Styled.IconButton id="map-legend-close-button" onClick={handleToggleLegend}>
                <Styled.CloseIcon />
            </Styled.IconButton>
        </Styled.LegendHeader>
        <Styled.OverflowWrapper>
            {displayedLegends[selectedLegendIndex].legendData.map((item, index) => {
                return <Styled.ItemWrapper
                    id={`map-legend-item-${index}`}
                    key={`legend-${index}`}
                >
                    <Styled.Color color={item.color} />
                    {displayLabel(item.label)}
                </Styled.ItemWrapper>
            })}
        </Styled.OverflowWrapper>
    </Styled.LegendWrapper>
}